import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SpaceBackground from '../components/SpaceBackground';
import { projects } from '../data/projects';
import { ArrowRight, Menu, X } from 'lucide-react';

const Projects = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="projects-page-v2" data-testid="projects-page">
      <SpaceBackground />

      <div className="projects-page-content">
        {/* Navbar - Same as Home */}
        <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="navbar">
          <div className="header-content">
            <div className="logo-container">
              <Link to="/">
                <img 
                  src="/moonrock-logo-light.png" 
                  alt="MoonRock Research & Development" 
                  className="navbar-logo-image"
                />
              </Link>
            </div>
            
            {/* Desktop Nav */}
            <nav className="nav-menu" data-testid="nav-menu">
              <Link to="/who-we-are" className="nav-link-page">Who We Are</Link>
              <Link to="/#how-we-work" className="nav-link-page">How We Work</Link>
              <Link to="/#industries" className="nav-link-page">Focus</Link>
              <Link to="/projects" className="nav-link-page active">Projects</Link>
            </nav>

            {/* CTA Button */}
            <div className="nav-cta-wrapper">
              <Link to="/contact">
                <Button className="nav-cta" data-testid="nav-cta">Partner With Us</Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
            <nav className="mobile-nav-links">
              <Link to="/who-we-are" onClick={() => setIsMobileMenuOpen(false)}>Who We Are</Link>
              <Link to="/#how-we-work" onClick={() => setIsMobileMenuOpen(false)}>How We Work</Link>
              <Link to="/#industries" onClick={() => setIsMobileMenuOpen(false)}>Focus</Link>
              <Link to="/projects" className="active" onClick={() => setIsMobileMenuOpen(false)}>Projects</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="mobile-nav-cta">Partner With Us</Button>
              </Link>
            </nav>
          </div>
        </header>

        <div className="projects-page-inner">
          {/* Page Header */}
          <header className="projects-page-header" style={{ paddingTop: '120px' }}>
            <h1 className="projects-page-title">Projects</h1>
            <p className="projects-page-subtitle">
              Selected ventures we've built with partners.
            </p>
          </header>

          {/* Projects Grid */}
          <div className="projects-grid">
            {projects.map((project) => (
              <div key={project.id} className="project-grid-card" data-testid={`project-card-${project.slug}`}>
                <div className="project-grid-image">
                  {project.imageSrc ? (
                    <img src={project.imageSrc} alt={project.name} />
                  ) : (
                    <div className="project-grid-placeholder">
                      <span className="project-grid-initials">
                        {project.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                  )}
                  <div className="project-grid-overlay"></div>
                </div>
                <div className="project-grid-content">
                  <h2 className="project-grid-name">{project.name}</h2>
                  <p className="project-grid-teaser">{project.teaser}</p>
                  <Link to={`/projects/${project.slug}`} className="project-grid-cta">
                    View Project
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="footer" data-testid="footer">
          <div className="footer-glass">
            <div className="footer-content">
              <div className="footer-grid">
                <div className="footer-brand">
                  <div className="footer-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/8gbuyg09_Full%20Logo%20Light%20TP%20space%20%281%29.png" 
                      alt="MoonRock R&D" 
                      style={{ height: '56px', width: 'auto', marginBottom: '14px', objectFit: 'contain' }}
                    />
                    <p className="footer-tagline" style={{ margin: 0, paddingLeft: '10px' }}>We Shape What Lasts.</p>
                  </div>
                </div>

                <div className="footer-links">
                  <h4 className="footer-heading">Quick Links</h4>
                  <ul>
                    <li><Link to="/">Home</Link></li>
                    <li><Link to="/who-we-are">Who We Are</Link></li>
                    <li><Link to="/#how-we-work">How We Work</Link></li>
                    <li><Link to="/#industries">Focus</Link></li>
                    <li><a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Projects</a></li>
                  </ul>
                </div>

                <div className="footer-contact">
                  <h4 className="footer-heading">Contact</h4>
                  <div className="contact-info">
                    <div className="contact-item">
                      <span>Suite 403, Bldg. 393, 18th November St, Muscat</span>
                    </div>
                    <div className="contact-item">
                      <a href="mailto:admin@moonrock.om">admin@moonrock.om</a>
                    </div>
                    <div className="contact-item">
                      <a href="tel:+96896607256">+968 9660 7256</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer-divider"></div>
              <div className="footer-bottom">
                <p className="footer-copyright">© 2026 MoonRock R&D. All rights reserved.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Projects;
